import { useReducer, useEffect, useRef, useMemo, useState } from 'react'
import { TUTOR_NO_SHOW } from './constants'
import moment from 'moment'
import { useInterval } from '../../../shared/useInterval'

const { STATES } = TUTOR_NO_SHOW

export const initialState = {
    status: STATES.IDLE,
    minutesSinceStart: 0,
    recoverySession: null,
    error: null,
    declinedByUser: false, // true if user chose credit over recovery
    claimRejection: null, // server's refusal of a tutor-absence claim
    retryAtMinute: null, // minutesSinceStart before which a refused claim must not fire again
}

/**
 * A too-early refusal that tells, on the server clock, when to retry: the
 * machine waits for it instead of giving up.
 */
export function isRetryableClaimRejection(rejection) {
    return rejection?.claim_rejected_reason === 'too_early' && rejection?.retry_after_seconds > 0
}

/**
 * Pure reducer for state machine transitions
 */
export function reducer(state, action) {
    switch (action.type) {
        case 'TUTOR_ABSENT':
            // Only transition if currently idle
            if (state.status === STATES.IDLE) {
                return { ...state, status: STATES.WAITING }
            }
            return state

        case 'CLAIM_REJECTED': {
            // The server re-derived the claim on its own clock and refused it.
            // Too early, it says how long to wait — measured server-side, so
            // re-arming cannot loop on a skewed device clock. Without that
            // delay (too late, or an older server) the refusal stays terminal:
            // a real no-show is still credited by the cron at T+20.
            if (isRetryableClaimRejection(action.payload)) {
                // The minutes of the refusal itself: the stored ones only move
                // when a rule of the clock flips (see `clockRules`).
                const minutesSinceStart = action.minutesSinceStart ?? state.minutesSinceStart
                return {
                    ...state,
                    status: STATES.WAITING,
                    minutesSinceStart,
                    recoverySession: null,
                    claimRejection: action.payload,
                    retryAtMinute: minutesSinceStart + action.payload.retry_after_seconds / 60,
                }
            }
            return { ...initialState, status: STATES.DONE, claimRejection: action.payload }
        }

        case 'TUTOR_ARRIVED':
            // Reset everything when tutor arrives
            return { ...initialState }

        case 'START_SEARCH':
            return { ...state, status: STATES.SEARCHING }

        case 'RECOVERY_FOUND':
            return {
                ...state,
                status: STATES.PROPOSING,
                recoverySession: action.payload
            }

        case 'NO_RECOVERY_FOUND':
            return { ...state, status: STATES.CREDITING, declinedByUser: false }

        case 'JOIN_RECOVERY':
            return { ...state, status: STATES.JOINING }

        case 'PREFER_CREDIT':
            return { ...state, status: STATES.CREDITING, declinedByUser: true }

        case 'COMPLETE':
            return { ...state, status: STATES.DONE }

        case 'ERROR':
            return { ...state, error: action.payload, status: STATES.CREDITING }

        case 'TICK':
            return { ...state, minutesSinceStart: action.payload }

        default:
            return state
    }
}

/**
 * Whether the automatic replacement search should fire now: when the server
 * says the replacement opens (`tutor_absence_dispatch_after_minutes`, which
 * waits for a tutor's announced arrival), AUTO_RECOVERY_DELAY when it says
 * nothing, and never before the moment a refused claim was told to retry.
 */
export function deriveShouldTriggerSearch(state, options = {}) {
    return state.status === STATES.WAITING && clockRules(state.minutesSinceStart, state, options).search
}

function searchThreshold(state, dispatchAfterMinutes) {
    return Math.max(dispatchAfterMinutes ?? TUTOR_NO_SHOW.AUTO_RECOVERY_DELAY, state.retryAtMinute ?? 0)
}

/**
 * The time rules of the wait, each written once: what `minutes` since the
 * start allows. The machine and its screens read the clock only through them,
 * so it is committed to the state only when one of them flips — a commit every
 * 10 s re-rendered the whole live for nothing, tutor present or not. The check
 * sits before the dispatch, not in the reducer: a dispatch that returns the
 * same state still re-runs the screen that holds the machine.
 */
export function clockRules(minutes, state, { dispatchAfterMinutes } = {}) {
    return {
        waiting: minutes >= TUTOR_NO_SHOW.WAITING_FROM,
        started: minutes >= 0,
        initialDialog: minutes >= TUTOR_NO_SHOW.INITIAL_DIALOG_DELAY,
        late: minutes >= TUTOR_NO_SHOW.WAITING_BANNER_DELAY,
        search: minutes >= searchThreshold(state, dispatchAfterMinutes),
    }
}

function sameRules(a, b) {
    return Object.keys(a).every((rule) => a[rule] === b[rule])
}

/** Minutes since the séance started; negative before it. */
function minutesSince(sessionStartTime) {
    return moment().diff(moment(sessionStartTime), 'minutes', true)
}

/**
 * State machine hook for tutor no-show logic
 * Manages timing and state transitions in a predictable way
 */
export function useTutorNoShowMachine({
    sessionStartTime,
    isTutorPresent,
    isStudentInSession,
    enabled = true,
    hasEverBeenPresent = false, // From Redux
    setTutorPresent = () => {}, // Redux dispatch action
    dispatchAfterMinutes, // server's tutor_absence_dispatch_after_minutes for today
}) {
    const [state, dispatch] = useReducer(reducer, initialState)
    const [isStable, setIsStable] = useState(false)
    const stabilizeTimeoutRef = useRef(null)
    const running = enabled && isStudentInSession && !!sessionStartTime
    const clock = clockRules(state.minutesSinceStart, state, { dispatchAfterMinutes })

    // Update immediately - can be negative if student arrives early
    useEffect(() => {
        if (running) dispatch({ type: 'TICK', payload: minutesSince(sessionStartTime) })
    }, [running, sessionStartTime])

    // Then read the clock every 10 seconds, and commit it only when a rule flips
    useInterval(
        () => {
            const minutes = minutesSince(sessionStartTime)
            if (sameRules(clockRules(minutes, state, { dispatchAfterMinutes }), clock)) return
            dispatch({ type: 'TICK', payload: minutes })
        },
        running ? 10000 : null
    )

    // Stabilization period: wait 3 seconds after connection to avoid flicker
    useEffect(() => {
        if (!enabled || !isStudentInSession) {
            setIsStable(false)
            if (stabilizeTimeoutRef.current) {
                clearTimeout(stabilizeTimeoutRef.current)
                stabilizeTimeoutRef.current = null
            }
            return
        }

        stabilizeTimeoutRef.current = setTimeout(() => {
            setIsStable(true)
        }, 3000)

        return () => {
            if (stabilizeTimeoutRef.current) {
                clearTimeout(stabilizeTimeoutRef.current)
                stabilizeTimeoutRef.current = null
            }
        }
    }, [enabled, isStudentInSession])

    // React to tutor presence changes (only after stabilization)
    useEffect(() => {
        if (!enabled || !isStudentInSession || !isStable) return

        // Track if tutor has ever been present (persist to Redux), once
        if (isTutorPresent && !hasEverBeenPresent) {
            setTutorPresent()
        }

        if (isTutorPresent && state.status !== STATES.IDLE && state.status !== STATES.DONE) {
            dispatch({ type: 'TUTOR_ARRIVED' })
        } else if (!isTutorPresent && !hasEverBeenPresent && state.status === STATES.IDLE && clock.waiting) {
            dispatch({ type: 'TUTOR_ABSENT' })
        }
    }, [isTutorPresent, isStudentInSession, enabled, state.status, clock.waiting, isStable, hasEverBeenPresent, setTutorPresent])

    // Derive UI states from machine state (no complex logic, just comparisons)
    const waitingNow = state.status === STATES.WAITING
    const shouldShowInitialDialog = waitingNow && clock.initialDialog && !clock.late
    const shouldShowBanner = waitingNow && clock.late
    const shouldTriggerSearch = waitingNow && clock.search

    // Stable action creators, in a stable object
    const actions = useMemo(
        () => ({
            startSearch: () => dispatch({ type: 'START_SEARCH' }),
            recoveryFound: (session) => dispatch({ type: 'RECOVERY_FOUND', payload: session }),
            noRecoveryFound: () => dispatch({ type: 'NO_RECOVERY_FOUND' }),
            joinRecovery: () => dispatch({ type: 'JOIN_RECOVERY' }),
            preferCredit: () => dispatch({ type: 'PREFER_CREDIT' }),
            claimRejected: (rejection) =>
                dispatch({
                    type: 'CLAIM_REJECTED',
                    payload: rejection,
                    minutesSinceStart: sessionStartTime ? minutesSince(sessionStartTime) : undefined,
                }),
            complete: () => dispatch({ type: 'COMPLETE' }),
            reportError: (error) => dispatch({ type: 'ERROR', payload: error }),
        }),
        [sessionStartTime]
    )

    return {
        // Current state
        status: state.status,
        // Only moves when a rule of `clockRules` flips: a new rule on the time is added there,
        // or it reads minutes late.
        minutesSinceStart: state.minutesSinceStart,
        recoverySession: state.recoverySession,
        error: state.error,
        declinedByUser: state.declinedByUser,
        claimRejection: state.claimRejection,

        // Derived booleans for UI (stable, no closures)
        isEarly: !clock.started,
        shouldShowInitialDialog,
        shouldShowBanner,
        shouldTriggerSearch,
        isSearching: state.status === STATES.SEARCHING,
        isProposing: state.status === STATES.PROPOSING,
        isJoining: state.status === STATES.JOINING,
        isCrediting: state.status === STATES.CREDITING,
        isDone: state.status === STATES.DONE,

        actions,
    }
}
